import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from "react-native";
import { Feather } from "@expo/vector-icons";
import { spacing } from "../../theme";
import { useColors, ThemeColors } from "../../hooks/useColors";
import { useVendorOrders } from "../../hooks/useVendorOrders";
import { useVendorProfile } from "../../hooks/useVendorProfile";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../types/navigation";
import { format } from "date-fns";
import { orderEvents } from "../../utils/orderEvents";
import { supabase } from "../../lib/supabase";
import { formatPrice } from "../../utils/format";
import { logger } from "../../utils/logger";
import { registerCacheClearFunction } from "../../utils/authCacheClear";

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Module-level cache to persist across component unmounts/remounts
// This prevents flash when navigating between screens
let cachedVendorOngoingOrders: VendorOrder[] = [];
let vendorHasEverShownContent = false;
let vendorHasEverLoaded = false;
// Track which user the cache belongs to - clear cache when user changes
let vendorCachedForUserId: string | null = null;

// Clear cache function - called on auth state changes to prevent cross-user data leakage
function clearVendorOrderCache(): void {
  console.log('[OngoingVendorOrderDisplay] Clearing module-level cache');
  cachedVendorOngoingOrders = [];
  vendorHasEverShownContent = false;
  vendorHasEverLoaded = false;
  vendorCachedForUserId = null;
}

// Register with central auth cache clearing system
registerCacheClearFunction(clearVendorOrderCache);

interface VendorOrder {
  id: string;
  created_at: string;
  updated_at?: string | null;
  status: string;
  total_amount: number;
  items: Array<{
    name: string;
    quantity: number;
    product_id?: string | null;
  }>;
  tracking_url?: string | null;
  delivery_status?: string | null;
  delivery_id?: string | null;
  parent_order_id?: string | null;
  buyer_id?: string | null;
}

const getStatusColor = (status: string) => {
  const jadeGreen = "#00A86B";
  const roseRed = "#F43F5E";
  switch (status.toLowerCase()) {
    case "cancelled":
    case "rejected":
    case "failed":
      return roseRed;
    default:
      return jadeGreen;
  }
};

const getStatusText = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "Pending Approval";
    case "confirmed":
      return "Order Confirmed";
    case "paid":
    case "succeeded":
      return "Needs Your Action";
    case "wrapping_up":
      return "Wrapping Up";
    case "packed":
      return "Packed";
    case "pickup_pending":
      return "Courier on the Way";
    case "pickup_complete":
      return "Pickup Complete";
    case "in_transit":
      return "In Transit";
    case "delivered":
      return "Delivered";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    case "rejected":
      return "Rejected";
    case "failed":
      return "Failed";
    default:
      // Convert snake_case to Title Case (e.g., "pickup_pending" -> "Pickup Pending")
      return status
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
  }
};

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "clock"; // Feather icon
    case "confirmed":
    case "paid":
    case "succeeded":
      return "check-circle";
    case "wrapping_up":
      return "package";
    case "packed":
      return "package"; // Using package for packed items
    case "pickup_pending":
      return "truck"; // Using truck as closest Feather alternative
    case "pickup_complete":
      return "check-circle";
    case "in_transit":
      return "truck";
    case "delivered":
    case "completed":
      return "check-circle";
    case "cancelled":
    case "rejected":
    case "failed":
      return "x-circle";
    default:
      return "circle";
  }
};

const isOngoingVendorOrder = (status: string): boolean => {
  // Use the same logic as buyer orders - filter out completed, delivered, cancelled, or rejected
  return Boolean(
    status &&
      !["completed", "delivered", "cancelled", "rejected"].includes(status.toLowerCase())
  );
};

// Get the next action for vendor order based on current status
const getNextAction = (status: string): { 
  label: string; 
  nextStatus: string | null; 
  icon: string;
  requiresConfirmation: boolean;
} | null => {
  switch (status.toLowerCase()) {
    case "pending":
      return { label: "Confirm Order", nextStatus: "confirmed", icon: "check", requiresConfirmation: true };
    case "paid":
      return { label: "Start Wrapping", nextStatus: "wrapping_up", icon: "package", requiresConfirmation: false };
    case "wrapping_up":
      return { label: "Mark Packed", nextStatus: "packed", icon: "archive", requiresConfirmation: false };
    // "packed" status - delivery starts automatically, no action needed
    // "pickup_pending" and "in_transit" - no action button for sellers (they can view details)
    default:
      return null;
  }
};

interface OngoingVendorOrderCardProps {
  order: VendorOrder;
  onPress: () => void;
  onStatusUpdate: (orderId: string, newStatus: string, parentOrderId?: string | null) => Promise<void>;
  onReject: (orderId: string, parentOrderId?: string | null, items?: VendorOrder['items']) => Promise<void>;
  colors: ThemeColors;
  styles: any;
  isUpdating: boolean;
  autoAcceptEnabled?: boolean;
}

// Countdown timer hook for order expiration (10 minutes for accept/reject)
const useCountdownTimer = (createdAt: string, expirationMinutes: number = 10) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    const createdTime = new Date(createdAt).getTime();
    const expirationTime = createdTime + (expirationMinutes * 60 * 1000);
    return Math.max(0, expirationTime - Date.now());
  });

  useEffect(() => {
    const createdTime = new Date(createdAt).getTime();
    const expirationTime = createdTime + (expirationMinutes * 60 * 1000);
    
    const interval = setInterval(() => {
      const remaining = Math.max(0, expirationTime - Date.now());
      setTimeRemaining(remaining);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [createdAt, expirationMinutes]);

  const minutes = Math.floor(timeRemaining / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);
  const isExpired = timeRemaining <= 0;
  const isUrgent = timeRemaining <= 2 * 60 * 1000; // Less than 2 minutes

  return { minutes, seconds, isExpired, isUrgent, timeRemaining };
};

// Countdown timer hook for packing phase (5 minutes from when status changed to wrapping_up)
// Uses startTime (timestamp) instead of createdAt string
const usePackingCountdownTimer = (startTime: number | null, expirationMinutes: number = 5) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    if (!startTime) return expirationMinutes * 60 * 1000;
    const expirationTime = startTime + (expirationMinutes * 60 * 1000);
    return Math.max(0, expirationTime - Date.now());
  });

  useEffect(() => {
    if (!startTime) {
      setTimeRemaining(expirationMinutes * 60 * 1000);
      return;
    }
    
    const expirationTime = startTime + (expirationMinutes * 60 * 1000);
    
    // Set initial value immediately
    setTimeRemaining(Math.max(0, expirationTime - Date.now()));
    
    const interval = setInterval(() => {
      const remaining = Math.max(0, expirationTime - Date.now());
      setTimeRemaining(remaining);
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [startTime, expirationMinutes]);

  const minutes = Math.floor(timeRemaining / 60000);
  const seconds = Math.floor((timeRemaining % 60000) / 1000);
  const isExpired = startTime !== null && timeRemaining <= 0;
  const isUrgent = timeRemaining <= 1 * 60 * 1000; // Less than 1 minute for packing

  return { minutes, seconds, isExpired, isUrgent, timeRemaining };
};

const OngoingVendorOrderCard: React.FC<OngoingVendorOrderCardProps> = ({
  order,
  onPress,
  onStatusUpdate,
  onReject,
  colors,
  styles,
  isUpdating,
  autoAcceptEnabled = false,
}) => {
  const statusColor = getStatusColor(order.status);
  const statusText = getStatusText(order.status);
  const statusIcon = getStatusIcon(order.status);
  const nextAction = getNextAction(order.status);
  const orderStatus = order.status.toLowerCase();
  // Show accept/reject for new orders that need vendor decision (pending or paid)
  // BUT NOT if auto-accept is enabled (orders will be auto-accepted)
  const needsVendorDecision = !autoAcceptEnabled && (orderStatus === "pending" || orderStatus === "paid" || orderStatus === "succeeded");
  
  // Countdown timer for orders needing decision (10 minutes from creation)
  const { minutes, seconds, isExpired, isUrgent } = useCountdownTimer(order.created_at, 10);
  
  // Track when order entered wrapping_up status for auto-pack timer
  const isWrappingUp = orderStatus === "wrapping_up";
  
  // Get the timestamp when the order status changed to wrapping_up
  // Use updated_at from the database (when status changed) instead of Date.now() to persist across refreshes
  const wrappingUpStartTime = useMemo(() => {
    if (!isWrappingUp) return null;
    // Use updated_at if available (this is when status changed to wrapping_up)
    if (order.updated_at) {
      return new Date(order.updated_at).getTime();
    }
    // Fallback to created_at if updated_at is not available
    return new Date(order.created_at).getTime();
  }, [isWrappingUp, order.updated_at, order.created_at]);
  
  // Packing countdown timer (5 minutes from when wrapping_up started)
  const packingTimer = usePackingCountdownTimer(
    wrappingUpStartTime,
    5 // 5 minutes
  );

  // Get first few items to display
  const displayItems = order.items.slice(0, 2);
  const remainingCount = order.items.length - displayItems.length;

  const handleAccept = async (e: any) => {
    e.stopPropagation();
    // Determine next status based on current status
    // If already paid, go to wrapping_up; if pending, go to confirmed
    const nextStatus = (orderStatus === "paid" || orderStatus === "succeeded") ? "wrapping_up" : "confirmed";
    const actionText = nextStatus === "wrapping_up" ? "Accept & Start Wrapping" : "Accept";
    
    Alert.alert(
      "Accept Order",
      "Are you sure you want to accept this order?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: actionText,
          onPress: () => onStatusUpdate(order.id, nextStatus, order.parent_order_id),
        },
      ]
    );
  };

  const handleReject = async (e: any) => {
    e.stopPropagation();
    Alert.alert(
      "Reject Order",
      "Are you sure you want to reject this order? This cannot be undone.",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Reject",
          style: "destructive",
          onPress: () => onReject(order.id, order.parent_order_id, order.items),
        },
      ]
    );
  };

  const handleActionPress = async (e: any) => {
    e.stopPropagation(); // Prevent card press
    
    if (!nextAction) return;
    
    // If no nextStatus, navigate to details (for "Start Delivery" or "Track Order")
    if (!nextAction.nextStatus) {
      onPress();
      return;
    }
    
    if (nextAction.requiresConfirmation) {
      Alert.alert(
        nextAction.label,
        `Are you sure you want to ${nextAction.label.toLowerCase()}?`,
        [
          { text: "Cancel", style: "cancel" },
          {
            text: "Confirm",
            onPress: () => onStatusUpdate(order.id, nextAction.nextStatus!, order.parent_order_id),
          },
        ]
      );
    } else {
      await onStatusUpdate(order.id, nextAction.nextStatus, order.parent_order_id);
    }
  };

  // Track if we've already triggered auto-reject for this order
  const autoRejectTriggeredRef = useRef(false);
  
  // Auto-reject when timer expires - runs the same logic as manual reject
  // This restores inventory, sends buyer notification, and triggers refund
  useEffect(() => {
    // Only auto-reject if:
    // 1. Timer has expired
    // 2. Order needs vendor decision (pending/paid/succeeded)
    // 3. Not already updating
    // 4. Haven't already triggered auto-reject for this order
    if (isExpired && needsVendorDecision && !isUpdating && !autoRejectTriggeredRef.current) {
      autoRejectTriggeredRef.current = true;
      logger.debug('VendorOrderDisplay', `⏰ [AUTO-REJECT] Timer expired for order ${order.id.slice(0, 8)}, auto-rejecting...`);
      
      // Call the same reject function that runs when vendor taps "Reject"
      // This will: restore inventory, update status, send notification, trigger refund
      onReject(order.id, order.parent_order_id, order.items);
    }
  }, [isExpired, needsVendorDecision, isUpdating, order.id, order.parent_order_id, order.items, onReject]);

  // Track if we've already triggered auto-pack for this order
  const autoPackTriggeredRef = useRef(false);
  
  // Reset auto-pack trigger when order leaves wrapping_up status
  useEffect(() => {
    if (!isWrappingUp) {
      autoPackTriggeredRef.current = false;
    }
  }, [isWrappingUp]);
  
  // Auto-pack when packing timer expires (5 minutes)
  useEffect(() => {
    // Only auto-pack if:
    // 1. Order is in wrapping_up status
    // 2. Packing timer has expired
    // 3. Not already updating
    // 4. Haven't already triggered auto-pack for this order
    if (isWrappingUp && packingTimer.isExpired && !isUpdating && !autoPackTriggeredRef.current) {
      autoPackTriggeredRef.current = true;
      logger.debug('VendorOrderDisplay', `📦 [AUTO-PACK] Timer expired for order ${order.id.slice(0, 8)}, auto-marking as packed...`);
      
      // Mark the order as packed
      onStatusUpdate(order.id, "packed", order.parent_order_id);
    }
  }, [isWrappingUp, packingTimer.isExpired, isUpdating, order.id, order.parent_order_id, onStatusUpdate]);

  return (
    <TouchableOpacity style={styles.orderCard} onPress={onPress}>
      {/* New Order: Show Accept/Reject buttons first */}
      {needsVendorDecision && (
        <View style={styles.pendingActionsContainer}>
          <View style={styles.pendingActionsHeader}>
            <Text style={styles.pendingActionsTitle}>New Order - Action Required</Text>
          </View>
          {/* Auto-reject warning message */}
          <View style={styles.autoRejectWarning}>
            <Feather name="info" size={14} color={colors.textLight} />
            <Text style={styles.autoRejectWarningText}>
              {isExpired 
                ? "This order is being auto-rejected. Customer will be refunded."
                : `Order will auto-reject in ${minutes}:${seconds.toString().padStart(2, '0')} if not accepted`
              }
            </Text>
          </View>
          <View style={styles.pendingActionsRow}>
            <TouchableOpacity
              style={[styles.acceptButton, isUpdating && styles.quickActionButtonDisabled]}
              onPress={handleAccept}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <>
                  <Feather name="check" size={16} color="#000" />
                  <Text style={styles.acceptButtonText}>Accept</Text>
                </>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.rejectButton, isUpdating && styles.quickActionButtonDisabled]}
              onPress={handleReject}
              disabled={isUpdating}
            >
              {isUpdating ? (
                <ActivityIndicator size="small" color="#000" />
              ) : (
                <>
                  <Feather name="x" size={16} color="#000" />
                  <Text style={styles.rejectButtonText}>Reject</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View style={styles.cardHeader}>
        <View style={styles.orderInfo}>
          <Text style={styles.orderId}>
            Order #{order.id.slice(-6).toUpperCase()}
          </Text>
          <Text style={styles.orderDate}>
            {format(new Date(order.created_at), "MMM dd, h:mm a")}
          </Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
          <Feather name={statusIcon as any} size={12} color="#000000" />
          <Text style={styles.statusText}>{statusText}</Text>
        </View>
      </View>

      <View style={styles.itemsContainer}>
        {displayItems.map((item, index) => (
          <Text key={index} style={styles.itemText}>
            {item.quantity}x {item.name}
          </Text>
        ))}
        {remainingCount > 0 && (
          <Text style={styles.moreItemsText}>
            +{remainingCount} more item{remainingCount > 1 ? "s" : ""}
          </Text>
        )}
      </View>

      <View style={styles.cardFooter}>
        <Text style={styles.totalAmount}>
          {formatPrice(order.total_amount)}
        </Text>
        <View style={styles.actionContainer}>
          <Text style={styles.viewDetailsText}>View Details</Text>
          <Feather name="chevron-right" size={16} color={colors.textLight} />
        </View>
      </View>

      {/* Wrapping Up: Show packing timer and Mark Packed button */}
      {isWrappingUp && (
        <View style={styles.packingActionsContainer}>
          <View style={styles.packingActionsHeader}>
            <Text style={styles.packingActionsTitle}>Packing In Progress</Text>
          </View>
          {/* Auto-pack info message */}
          <View style={styles.autoPackInfo}>
            <Feather name="info" size={14} color={colors.textLight} />
            <Text style={styles.autoPackInfoText}>
              {packingTimer.isExpired 
                ? "Order is being auto-marked as packed..."
                : `Order will auto-pack in ${packingTimer.minutes}:${packingTimer.seconds.toString().padStart(2, '0')}`
              }
            </Text>
          </View>
          <TouchableOpacity
            style={[
              styles.packButton,
              isUpdating && styles.quickActionButtonDisabled,
            ]}
            onPress={handleActionPress}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <ActivityIndicator size="small" color="#000" />
            ) : (
              <>
                <Feather name="archive" size={14} color="#000" />
                <Text style={styles.packButtonText}>Mark Packed</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      )}

      {/* Quick Action Button - only show for orders that don't need vendor decision and are not wrapping_up */}
      {!needsVendorDecision && !isWrappingUp && nextAction && (
        <TouchableOpacity
          style={[
            styles.quickActionButton,
            isUpdating && styles.quickActionButtonDisabled,
          ]}
          onPress={handleActionPress}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <ActivityIndicator size="small" color="#000" />
          ) : (
            <>
              <Feather name={nextAction.icon as any} size={14} color="#000" />
              <Text style={styles.quickActionText}>{nextAction.label}</Text>
            </>
          )}
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );
};

// Helper function to check if store is currently open
const isStoreCurrentlyOpen = (deliveryHours: any): boolean => {
  if (!deliveryHours) return false;
  if (deliveryHours.is24_7) return true;
  
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const now = new Date();
  const dayKey = days[now.getDay()];
  const dayHours = deliveryHours[dayKey];
  
  if (!dayHours || !dayHours.isOpen) return false;
  
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const [openH, openM] = (dayHours.openTime || '00:00').split(':').map(Number);
  const [closeH, closeM] = (dayHours.closeTime || '23:59').split(':').map(Number);
  
  const openMinutes = openH * 60 + openM;
  const closeMinutes = closeH * 60 + closeM;
  
  return currentMinutes >= openMinutes && currentMinutes <= closeMinutes;
};

export const OngoingVendorOrderDisplay: React.FC = () => {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<NavigationProp>();
  const { data: vendor, isLoading: isVendorLoading } = useVendorProfile();
  const { orders, loading, error, refetch } = useVendorOrders();
  
  // State for tracking which order is being updated
  const [updatingOrderId, setUpdatingOrderId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Track user changes and clear cache when user switches
  // This prevents showing another user's orders after logout/login
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;
      
      // If user changed, clear the module-level cache
      if (userId !== vendorCachedForUserId) {
        logger.debug('VendorOrderDisplay', `🔄 [ONGOING-VENDOR-ORDER-DISPLAY] User changed from ${vendorCachedForUserId?.slice(0, 8) ?? 'none'} to ${userId?.slice(0, 8) ?? 'none'}, clearing cache`);
        cachedVendorOngoingOrders = [];
        vendorHasEverShownContent = false;
        vendorHasEverLoaded = false;
        vendorCachedForUserId = userId;
      }
      setCurrentUserId(userId);
    };
    
    checkUser();
    
    // Also listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const userId = session?.user?.id ?? null;
      if (userId !== vendorCachedForUserId) {
        logger.debug('VendorOrderDisplay', `🔄 [ONGOING-VENDOR-ORDER-DISPLAY] Auth state changed, user: ${userId?.slice(0, 8) ?? 'none'}, clearing cache`);
        cachedVendorOngoingOrders = [];
        vendorHasEverShownContent = false;
        vendorHasEverLoaded = false;
        vendorCachedForUserId = userId;
      }
      setCurrentUserId(userId);
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  // Check if vendor has auto-accept enabled
  const autoAcceptEnabled = useMemo(() => {
    if (!vendor?.metadata) return false;
    const metadata = vendor.metadata as { auto_accept_orders?: boolean };
    return metadata.auto_accept_orders === true && isStoreCurrentlyOpen(vendor.delivery_hours);
  }, [vendor?.metadata, vendor?.delivery_hours]);
  
  // Update module-level flag when vendor is loaded (persists across remounts)
  if (vendor) {
    vendorHasEverLoaded = true;
  }
  
  // Use module-level cache to persist across unmounts
  // This is the key fix for preventing flash during navigation
  const effectiveOrders = orders.length > 0 ? orders : cachedVendorOngoingOrders;
  
  // Keep a ref to orders for stable comparison in polling effect
  const ordersRef = useRef(effectiveOrders);
  ordersRef.current = effectiveOrders;

  // Generate a unique ID for this component instance to track in logs
  const [componentId] = useState(() =>
    Math.random().toString(36).substring(2, 11)
  );

  // Function to update order status
  const handleStatusUpdate = async (orderId: string, newStatus: string, parentOrderId?: string | null) => {
    setUpdatingOrderId(orderId);
    try {
      // Update vendor_orders table
      const { error: vendorError } = await supabase
        .from("vendor_orders")
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq("id", orderId);
      
      if (vendorError) throw vendorError;

      // Also update the main orders table if parent_order_id exists
      if (parentOrderId) {
        const { error: orderError } = await supabase
          .from("orders")
          .update({ status: newStatus, updated_at: new Date().toISOString() })
          .eq("id", parentOrderId);
        
        if (orderError) {
          logger.warn('VendorOrderDisplay', "Failed to update orders table:", orderError);
          // Don't throw here - vendor_orders update succeeded
        }
      }

      logger.debug('VendorOrderDisplay', `✅ [ONGOING-VENDOR-ORDER-DISPLAY] Order ${orderId.slice(0, 8)} status updated to ${newStatus}`);
      
      // If marked as packed, automatically navigate to start delivery
      if (newStatus === "packed") {
        logger.debug('VendorOrderDisplay', `🚚 [ONGOING-VENDOR-ORDER-DISPLAY] Order packed, auto-starting delivery...`);
        setUpdatingOrderId(null);
        // Navigate to order details with auto-start delivery flag
        (navigation as any).navigate("VendorOrderDetails", {
          vendorOrderId: orderId,
          autoStartDelivery: true,
        });
        return;
      }
      
      // Refetch to update the list
      refetch();
    } catch (e: any) {
      logger.error('VendorOrderDisplay', `❌ [ONGOING-VENDOR-ORDER-DISPLAY] Error updating order status:`, e);
      Alert.alert("Error", e?.message || "Failed to update order status");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Function to reject order
  const handleRejectOrder = async (orderId: string, parentOrderId?: string | null, orderItems?: VendorOrder['items']) => {
    setUpdatingOrderId(orderId);
    try {
      // Get buyer_id, items, and payment_intent_id before updating
      const { data: orderData } = await supabase
        .from("vendor_orders")
        .select("buyer_id, items, order_id")
        .eq("id", orderId)
        .single();

      // Get payment_intent_id and user_id from the parent order for refund and notification
      let paymentIntentId: string | null = null;
      let buyerUserId: string | null = null;
      const parentId = parentOrderId || orderData?.order_id;
      if (parentId) {
        const { data: parentOrder } = await supabase
          .from("orders")
          .select("payment_intent_id, user_id")
          .eq("id", parentId)
          .single();
        paymentIntentId = parentOrder?.payment_intent_id || null;
        // Use buyer_id from vendor_orders, or fallback to user_id from orders
        buyerUserId = orderData?.buyer_id || parentOrder?.user_id || null;
      } else {
        buyerUserId = orderData?.buyer_id || null;
      }

      // Restore inventory for all items in the order
      const itemsToRestore = orderItems || orderData?.items || [];
      logger.debug('VendorOrderDisplay', `🔄 [ONGOING-VENDOR-ORDER-DISPLAY] Restoring inventory for ${itemsToRestore.length} items`);
      
      for (const item of itemsToRestore) {
        const productId = item.product_id;
        const quantity = item.quantity || 0;
        
        logger.debug('VendorOrderDisplay', `🔄 [ONGOING-VENDOR-ORDER-DISPLAY] Processing item:`, { product_id: productId, quantity, name: item.name });
        
        if (productId && quantity > 0) {
          // Get current product quantity and status
          const { data: product, error: fetchErr } = await supabase
            .from("products")
            .select("quantity, status")
            .eq("id", productId)
            .single();
          
          if (fetchErr) {
            logger.error('VendorOrderDisplay', `❌ [ONGOING-VENDOR-ORDER-DISPLAY] Failed to fetch product ${productId}:`, fetchErr);
            continue;
          }
          
          const currentQuantity = Number(product?.quantity ?? 0);
          const newQuantity = currentQuantity + quantity;
          
          // Prepare update data - restore status to active if it was sold
          const updateData: { quantity: number; status?: string } = { quantity: newQuantity };
          if (product?.status === "sold") {
            updateData.status = "active";
          }
          
          const { error: updateErr } = await supabase
            .from("products")
            .update(updateData)
            .eq("id", productId);
          
          if (updateErr) {
            logger.error('VendorOrderDisplay', `❌ [ONGOING-VENDOR-ORDER-DISPLAY] Failed to restore quantity for product ${productId}:`, updateErr);
          } else {
            logger.debug('VendorOrderDisplay', `✅ [ONGOING-VENDOR-ORDER-DISPLAY] Restored ${quantity} units to product ${productId} (new quantity: ${newQuantity})`);
          }
        }
      }

      // Update vendor_orders table
      const { error: vendorError } = await supabase
        .from("vendor_orders")
        .update({ status: "rejected", updated_at: new Date().toISOString() })
        .eq("id", orderId);
      
      if (vendorError) throw vendorError;

      // Also update the main orders table if parent_order_id exists
      if (parentId) {
        const { error: orderError } = await supabase
          .from("orders")
          .update({ status: "rejected", updated_at: new Date().toISOString() })
          .eq("id", parentId);
        
        if (orderError) {
          logger.warn('VendorOrderDisplay', "Failed to update orders table:", orderError);
        }

        // Process refund immediately if we have a payment_intent_id
        if (paymentIntentId) {
          logger.debug('VendorOrderDisplay', `💰 [REFUND] Processing refund for payment_intent: ${paymentIntentId}`);
          try {
            const refundResult = await supabase.functions.invoke("refund-payment", {
              body: {
                payment_intent_id: paymentIntentId,
                reason: "requested_by_customer",
                metadata: {
                  vendor_order_id: orderId,
                  parent_order_id: parentId,
                  rejection_reason: "vendor_rejected",
                },
              },
            });

            if (refundResult.error || refundResult.data?.error) {
              logger.warn('VendorOrderDisplay', "⚠️ [REFUND] Refund request failed:", refundResult.error || refundResult.data?.error);
              // Don't fail the rejection if refund fails - cron job will pick it up
            } else {
              logger.debug('VendorOrderDisplay', `✅ [REFUND] Refund processed:`, refundResult.data);
            }
          } catch (refundError) {
            logger.warn('VendorOrderDisplay', "⚠️ [REFUND] Failed to process refund:", refundError);
            // Don't fail the rejection if refund fails - cron job will pick it up
          }
        } else {
          logger.debug('VendorOrderDisplay', "⚠️ [REFUND] No payment_intent_id found, skipping refund (cron job will handle)");
        }

        // Send notification to buyer about rejection
        if (buyerUserId) {
          logger.debug('VendorOrderDisplay', `📱 [NOTIFICATION] Sending rejection notification to buyer: ${buyerUserId}`);
          try {
            const notifResult = await supabase.functions.invoke("send-notification", {
              body: {
                user_id: buyerUserId,
                title: "Order Cancelled",
                body: "Your order has been cancelled. A full refund has been initiated.",
                notification_type: "order_status", // Must be notification_type, not type
                data: {
                  order_id: parentId,
                  status: "rejected",
                },
              },
            });
            if (notifResult.error) {
              logger.warn('VendorOrderDisplay', "⚠️ [NOTIFICATION] Notification function error:", notifResult.error);
            } else {
              logger.debug('VendorOrderDisplay', "✅ [NOTIFICATION] Rejection notification sent to buyer:", notifResult.data);
            }

            // Send SMS to buyer if they don't have native app (web-only users)
            try {
              // Get buyer's phone from their default delivery address
              const { data: buyerAddress } = await supabase
                .from("delivery_addresses")
                .select("phone_number")
                .eq("user_id", buyerUserId)
                .eq("is_default", true)
                .single();

              const buyerPhone = buyerAddress?.phone_number;

              if (buyerPhone) {
                const { data: smsResult } = await supabase.functions.invoke("send-sms", {
                  body: {
                    to: buyerPhone,
                    message: "Your JadeTrade order was cancelled by the seller. Full refund is being processed.",
                    user_id: buyerUserId,
                  },
                });

                if (smsResult?.skipped) {
                  logger.debug('VendorOrderDisplay', "📱 [SMS] Skipped:", smsResult.skip_reason);
                } else if (smsResult?.success) {
                  logger.debug('VendorOrderDisplay', "📱 [SMS] Sent to buyer");
                } else {
                  logger.warn('VendorOrderDisplay', "⚠️ [SMS] Failed:", smsResult?.error);
                }
              } else {
                logger.debug('VendorOrderDisplay', "📱 [SMS] No phone number for buyer, skipping SMS");
              }
            } catch (smsError) {
              logger.warn('VendorOrderDisplay', "⚠️ [SMS] Failed to send SMS:", smsError);
            }
          } catch (notifError) {
            logger.warn('VendorOrderDisplay', "⚠️ [NOTIFICATION] Failed to send rejection notification:", notifError);
          }
        } else {
          logger.warn('VendorOrderDisplay', "⚠️ [NOTIFICATION] No buyer user ID found, cannot send notification");
        }
      }

      logger.debug('VendorOrderDisplay', `✅ [ONGOING-VENDOR-ORDER-DISPLAY] Order ${orderId.slice(0, 8)} rejected`);
      
      // Immediately remove the rejected order from the module-level cache
      // This prevents the order from reappearing briefly during refetch
      cachedVendorOngoingOrders = cachedVendorOngoingOrders.filter(o => o.id !== orderId);
      
      // Refetch to update the list
      refetch();
    } catch (e: any) {
      logger.error('VendorOrderDisplay', `❌ [ONGOING-VENDOR-ORDER-DISPLAY] Error rejecting order:`, e);
      Alert.alert("Error", e?.message || "Failed to reject order");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Listen for order events to refresh data
  useEffect(() => {
    const unsubscribe = orderEvents.subscribe(() => {
      refetch(); // Refetch orders data - state update handles re-render
    });

    return unsubscribe;
  }, [refetch]);

  // Auto-accept orders when auto-accept is enabled (client-side fallback)
  // This handles orders that came in before the edge function could process them
  useEffect(() => {
    if (!autoAcceptEnabled) return;
    
    const pendingOrders = effectiveOrders.filter(
      (order) => ["pending", "paid", "succeeded"].includes(order.status.toLowerCase())
    );
    
    if (pendingOrders.length === 0) return;
    
    logger.debug('VendorOrderDisplay', `🤖 [AUTO-ACCEPT] Auto-accept enabled, processing ${pendingOrders.length} pending order(s)...`);
    
    // Auto-accept each pending order
    pendingOrders.forEach(async (order) => {
      logger.debug('VendorOrderDisplay', `🤖 [AUTO-ACCEPT] Auto-accepting order ${order.id.slice(0, 8)}...`);
      const nextStatus = (order.status.toLowerCase() === "paid" || order.status.toLowerCase() === "succeeded") 
        ? "wrapping_up" 
        : "confirmed";
      
      try {
        // Update vendor_orders table
        const { error: vendorError } = await supabase
          .from("vendor_orders")
          .update({ status: nextStatus, updated_at: new Date().toISOString() })
          .eq("id", order.id);
        
        if (vendorError) {
          logger.error('VendorOrderDisplay', `❌ [AUTO-ACCEPT] Error auto-accepting order:`, vendorError);
          return;
        }

        // Also update the main orders table if parent_order_id exists
        if (order.parent_order_id) {
          await supabase
            .from("orders")
            .update({ status: nextStatus, updated_at: new Date().toISOString() })
            .eq("id", order.parent_order_id);
        }

        logger.debug('VendorOrderDisplay', `✅ [AUTO-ACCEPT] Order ${order.id.slice(0, 8)} auto-accepted to ${nextStatus}`);
      } catch (e) {
        logger.error('VendorOrderDisplay', `❌ [AUTO-ACCEPT] Error:`, e);
      }
    });
    
    // Refetch after auto-accepting
    setTimeout(() => refetch(), 500);
  }, [autoAcceptEnabled, effectiveOrders, refetch]);

  // Note: Removed useFocusEffect refetch - real-time subscriptions handle updates
  // and the focus refetch was causing layout flash when navigating back

  // Filter for ongoing vendor orders using the same logic as buyer orders
  // Use effectiveOrders to prevent flash when data briefly empties
  const ongoingOrders = useMemo(
    () => effectiveOrders.filter((order) => isOngoingVendorOrder(order.status)),
    [effectiveOrders]
  );
  
  // Stable reference for order IDs that need polling
  const ongoingOrderIds = useMemo(
    () => ongoingOrders.map(o => o.id).join(','),
    [ongoingOrders]
  );

  // Poll order status every 10 seconds for ongoing orders with delivery_id
  useEffect(() => {
    logger.debug('VendorOrderDisplay', 
      `🔍 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Polling effect triggered. Ongoing orders: ${ongoingOrders.length}`
    );

    if (ongoingOrders.length === 0) {
      logger.debug('VendorOrderDisplay', 
        `⏸️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] No ongoing orders, skipping polling setup`
      );
      return; // No ongoing orders, no need to poll
    }

    // Get orders with delivery_id that need status updates
    const ordersWithDelivery = ongoingOrders.filter(
      (order) => order.delivery_id && !["completed", "delivered", "cancelled", "rejected"].includes(order.status)
    );

    logger.debug('VendorOrderDisplay', 
      `🔍 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Orders with delivery_id: ${ordersWithDelivery.length} out of ${ongoingOrders.length} ongoing orders`
    );

    if (ordersWithDelivery.length === 0) {
      logger.debug('VendorOrderDisplay', 
        `⏸️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] No orders with delivery_id, skipping polling setup`
      );
      return; // No orders with delivery_id to poll
    }

    logger.debug('VendorOrderDisplay', 
      `🔄 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Starting polling for ${ordersWithDelivery.length} orders with delivery_id`,
      ordersWithDelivery.map((o) => ({
        id: o.id.slice(0, 8),
        status: o.status,
        delivery_id: o.delivery_id?.slice(0, 8),
      }))
    );

    const pollOrderStatuses = async () => {
      const pollStartTime = new Date().toISOString();
      logger.debug('VendorOrderDisplay', 
        `⏰ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Polling started at ${pollStartTime} for ${ordersWithDelivery.length} orders`
      );

      try {
        // Fetch latest vendor order statuses from database
        const orderIds = ordersWithDelivery.map((o) => o.id);
        logger.debug('VendorOrderDisplay', 
          `📊 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Fetching statuses for orders:`,
          orderIds.map((id) => id.slice(0, 8))
        );

        const { data: latestOrders, error: fetchError } = await supabase
          .from("vendor_orders")
          .select("id, status, delivery_id")
          .in("id", orderIds);

        if (fetchError) {
          logger.error('VendorOrderDisplay', 
            `❌ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Error fetching latest orders:`,
            fetchError
          );
          return;
        }

        logger.debug('VendorOrderDisplay', 
          `✅ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Fetched ${latestOrders?.length || 0} orders from database`
        );

        if (latestOrders) {
          // Check for status changes and update if needed
          let hasUpdates = false;
          for (const latestOrder of latestOrders) {
            const currentOrder = ordersRef.current.find((o) => o.id === latestOrder.id);
            const currentStatus = currentOrder?.status;
            const newStatus = latestOrder.status;

            logger.debug('VendorOrderDisplay', 
              `🔍 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Order ${latestOrder.id.slice(0, 8)}: current=${currentStatus}, db=${newStatus}`
            );

            if (currentStatus !== newStatus) {
              logger.debug('VendorOrderDisplay', 
                `🔄 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Order ${latestOrder.id.slice(0, 8)} status changed: ${currentStatus} -> ${newStatus}`
              );
              hasUpdates = true;
            }

            // If order has delivery_id, also check Uber API for delivered status
            if (latestOrder.delivery_id && newStatus !== "completed") {
              logger.debug('VendorOrderDisplay', 
                `🚚 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Checking Uber API for order ${latestOrder.id.slice(0, 8)} (delivery_id: ${latestOrder.delivery_id.slice(0, 8)})`
              );

              try {
                const { data: uberData, error: uberError } =
                  await supabase.functions.invoke("uber-delivery", {
                    body: { delivery_id: latestOrder.delivery_id },
                  });

                if (uberError) {
                  // Handle different error types gracefully
                  const errorMessage = uberError.message || String(uberError);
                  const is404 = errorMessage.includes("404") || errorMessage.includes("not found");
                  const is429 = errorMessage.includes("429") || errorMessage.includes("rate limit");
                  
                  if (is404) {
                    // Delivery not found - might be deleted or invalid, log but don't spam
                    logger.debug('VendorOrderDisplay', 
                      `ℹ️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Delivery not found for order ${latestOrder.id.slice(0, 8)} (delivery_id: ${latestOrder.delivery_id?.slice(0, 8)})`
                    );
                  } else if (is429) {
                    // Rate limited - log but don't spam, will retry on next poll
                    logger.debug('VendorOrderDisplay', 
                      `⏳ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Rate limited for order ${latestOrder.id.slice(0, 8)}, will retry later`
                    );
                  } else {
                    // Other errors - log but don't spam
                    logger.warn('VendorOrderDisplay', 
                      `⚠️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Uber API error for order ${latestOrder.id.slice(0, 8)}:`,
                      errorMessage
                    );
                  }
                  // Continue to next order instead of breaking the loop
                  continue;
                }

                // Check if response has error field (even if no uberError)
                if (uberData?.error) {
                  const errorMessage = uberData.error || uberData.details;
                  logger.warn('VendorOrderDisplay', 
                    `⚠️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Uber API returned error for order ${latestOrder.id.slice(0, 8)}:`,
                    errorMessage
                  );
                  continue;
                }

                // Map Uber status to our internal status
                const uberStatus = uberData?.uber_status?.status;
                if (uberStatus) {
                  logger.debug('VendorOrderDisplay', 
                    `📦 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Uber API response for order ${latestOrder.id.slice(0, 8)}:`,
                    uberStatus
                  );

                  let targetStatus: string | null = null;
                  
                  if (uberStatus === "delivered") {
                    targetStatus = "completed";
                  } else if (uberStatus === "pickup_complete") {
                    targetStatus = "in_transit";
                  } else if (uberStatus === "dropoff") {
                    targetStatus = "pickup_complete";
                  } else if (uberStatus === "pickup" || uberStatus === "shopping_completed") {
                    targetStatus = "pickup_pending";
                  }
                  
                  // Update database if Uber status indicates a change
                  if (targetStatus && newStatus !== targetStatus) {
                    logger.debug('VendorOrderDisplay', 
                      `✅ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Uber status "${uberStatus}" maps to "${targetStatus}" but DB shows "${newStatus}" for order ${latestOrder.id.slice(0, 8)}, updating database...`
                    );

                    // Update vendor order status
                    const { error: updateError } = await supabase
                      .from("vendor_orders")
                      .update({
                        status: targetStatus,
                        updated_at: new Date().toISOString(),
                      })
                      .eq("id", latestOrder.id);

                    if (updateError) {
                      logger.error('VendorOrderDisplay', 
                        `❌ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Error updating order to ${targetStatus}:`,
                        updateError
                      );
                    } else {
                      logger.debug('VendorOrderDisplay', 
                        `✅ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Successfully updated order ${latestOrder.id.slice(0, 8)} to ${targetStatus}`
                      );
                      hasUpdates = true;
                    }
                  }
                } else {
                  // No status in response - might be a different response format
                  logger.debug('VendorOrderDisplay', 
                    `ℹ️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] No status in Uber response for order ${latestOrder.id.slice(0, 8)}`
                  );
                }
              } catch (err) {
                // Catch any unexpected errors
                const errorMessage = err instanceof Error ? err.message : String(err);
                logger.warn('VendorOrderDisplay', 
                  `⚠️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Error checking Uber API for order ${latestOrder.id.slice(0, 8)}:`,
                  errorMessage
                );
                // Continue to next order instead of breaking
                continue;
              }
            }
          }

          // Refetch orders if there were any updates
          if (hasUpdates) {
            logger.debug('VendorOrderDisplay', 
              `🔄 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Status updates detected, refetching orders...`
            );
            refetch();
          } else {
            logger.debug('VendorOrderDisplay', 
              `✅ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] No status updates detected in this poll`
            );
          }
        }
      } catch (err) {
        logger.error('VendorOrderDisplay', `❌ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Error in pollOrderStatuses:`, err);
      }

      const pollEndTime = new Date().toISOString();
      logger.debug('VendorOrderDisplay', 
        `⏰ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Polling completed at ${pollEndTime}`
      );
    };

    // Initial poll
    logger.debug('VendorOrderDisplay', `🚀 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Running initial poll...`);
    pollOrderStatuses();

    // Poll every 10 seconds
    logger.debug('VendorOrderDisplay', `⏱️ [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Setting up 10-second polling interval`);
    const pollInterval = setInterval(pollOrderStatuses, 10000);

    return () => {
      logger.debug('VendorOrderDisplay', `🛑 [ONGOING-VENDOR-ORDER-DISPLAY-${componentId}] Stopping polling and clearing interval`);
      clearInterval(pollInterval);
    };
  }, [ongoingOrderIds, refetch, componentId]);

  const handleOrderPress = (order: VendorOrder, autoStartDelivery?: boolean) => {
    // Navigate to VendorOrderDetails screen with the order ID
    (navigation as any).navigate("VendorOrderDetails", {
      vendorOrderId: order.id,
      autoStartDelivery: autoStartDelivery || false,
    });
  };

  const handleViewAllOrders = () => {
    // Navigate to Orders screen with Sales tab selected
    (navigation as any).navigate("Orders", { initialTab: "pending" });
  };

  // Update module-level cache - always sync with current ongoing orders
  // This ensures completed orders are removed from the cache
  useEffect(() => {
    // Only update cache if we have real data from the API (not during initial load)
    // We check if orders array exists (even if empty after completion)
    if (orders.length > 0 || vendorHasEverShownContent) {
      cachedVendorOngoingOrders = ongoingOrders;
    }
    if (ongoingOrders.length > 0) {
      vendorHasEverShownContent = true;
    }
  }, [ongoingOrders, orders.length]);

  // Don't show if not a vendor (early return)
  // Use vendorHasEverLoaded (module-level) to prevent flash when isVendorLoading briefly becomes true on remount
  if (isVendorLoading && !vendorHasEverLoaded) {
    // Only hide on initial load, not on subsequent remounts
    return null;
  }

  if (!vendor && !vendorHasEverLoaded) {
    // User is not a vendor, don't show vendor orders
    // Only check on initial load to prevent flash
    return null;
  }

  // Use cached orders if current is empty but we've shown content before
  const displayOrders = ongoingOrders.length > 0 ? ongoingOrders : cachedVendorOngoingOrders;
  
  // Don't show if truly no orders (including cache)
  if (displayOrders.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>

      <View style={styles.ordersContainer}>
        {displayOrders.slice(0, 2).map((order) => (
          <OngoingVendorOrderCard
            key={order.id}
            order={order}
            onPress={() => handleOrderPress(order)}
            onStatusUpdate={handleStatusUpdate}
            onReject={handleRejectOrder}
            colors={colors}
            styles={styles}
            isUpdating={updatingOrderId === order.id}
            autoAcceptEnabled={autoAcceptEnabled}
          />
        ))}

        {displayOrders.length > 2 && (
          <TouchableOpacity
            style={styles.showMoreButton}
            onPress={handleViewAllOrders}
          >
            <Text style={styles.showMoreText}>
              View {displayOrders.length - 2} more ongoing orders
            </Text>
            <Feather name="arrow-right" size={16} color={colors.primary} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: spacing.lg,
      paddingTop: 0,
      paddingBottom: spacing.md,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    sectionTitle: {
      fontSize: 20,
      fontWeight: "600",
      color: colors.text,
    },
    viewAllText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "500",
    },
    ordersContainer: {
      gap: spacing.sm,
    },
    orderCard: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.03,
      shadowRadius: 8,
      elevation: 2,
    },
    cardHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: spacing.md,
    },
    orderInfo: {
      flex: 1,
    },
    orderId: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
      marginBottom: 2,
    },
    orderDate: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    statusBadge: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
      gap: 4,
    },
    statusText: {
      fontSize: 11,
      fontWeight: "700",
      color: "#000000",
    },
    itemsContainer: {
      marginBottom: spacing.md,
      paddingHorizontal: 2,
    },
    itemText: {
      fontSize: 14,
      color: colors.text,
      marginBottom: 4,
      fontWeight: "500",
    },
    moreItemsText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontStyle: "italic",
    },
    cardFooter: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: spacing.md,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    totalAmount: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
    },
    actionContainer: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
    },
    viewDetailsText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "700",
    },
    showMoreButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      height: 52,
      backgroundColor: colors.surface,
      borderRadius: 26,
      borderWidth: 1,
      borderColor: colors.border,
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    showMoreText: {
      fontSize: 14,
      color: colors.primary,
      fontWeight: "700",
    },
    quickActionButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary,
      height: 44,
      borderRadius: 22,
      marginTop: spacing.md,
      gap: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    quickActionButtonDisabled: {
      opacity: 0.6,
    },
    quickActionText: {
      color: "#000",
      fontSize: 14,
      fontWeight: "700",
    },
    // Pending order actions
    pendingActionsContainer: {
      backgroundColor: colors.primary + "15",
      borderRadius: 16,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.primary + "30",
    },
    pendingActionsHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    pendingActionsTitle: {
      fontSize: 14,
      fontWeight: "800",
      color: colors.primary,
      letterSpacing: -0.2,
    },
    autoRejectWarning: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
      gap: 6,
    },
    autoRejectWarningText: {
      flex: 1,
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: "600",
    },
    countdownBadge: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.error,
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
      gap: 4,
    },
    countdownBadgeUrgent: {
      backgroundColor: colors.error,
    },
    countdownBadgeExpired: {
      backgroundColor: colors.error,
    },
    countdownText: {
      fontSize: 11,
      color: "#000",
      fontWeight: "700",
    },
    countdownTextUrgent: {
      color: "#000",
    },
    countdownTextExpired: {
      fontSize: 11,
      color: "#000",
    },
    pendingActionsRow: {
      flexDirection: "row",
      gap: spacing.md,
    },
    acceptButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary,
      height: 44,
      borderRadius: 22,
      gap: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    acceptButtonText: {
      color: "#000",
      fontSize: 14,
      fontWeight: "700",
    },
    rejectButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.error,
      height: 44,
      borderRadius: 22,
      gap: 8,
      shadowColor: colors.error,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    rejectButtonText: {
      color: "#000",
      fontSize: 14,
      fontWeight: "700",
    },
    // Packing phase styles
    packingActionsContainer: {
      backgroundColor: colors.primary + "15",
      borderRadius: 16,
      padding: spacing.md,
      marginTop: spacing.md,
      borderWidth: 1,
      borderColor: colors.primary + "30",
    },
    packingActionsHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    packingActionsTitle: {
      fontSize: 14,
      fontWeight: "800",
      color: colors.primary,
      letterSpacing: -0.2,
    },
    autoPackInfo: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
      gap: 6,
    },
    autoPackInfoText: {
      flex: 1,
      fontSize: 12,
      color: colors.textSecondary,
      fontWeight: "600",
    },
    packButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary,
      height: 44,
      borderRadius: 22,
      gap: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 4,
    },
    packButtonText: {
      color: "#000",
      fontSize: 14,
      fontWeight: "700",
    },
  });
